import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getCurrentImpersonation } from '../utils/impersonation';

const STORAGE_PREFIX = 'ovo-table-config-';

function loadConfig(tableKey) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConfig(tableKey, config) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tableKey, JSON.stringify(config));
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Hook for configurable table columns with localStorage persistence.
 *
 * @param {string} tableKey - Unique key for localStorage (e.g., 'routes-columns')
 * @param {Array} allColumns - Full column definitions array from the table component
 * @param {Array} fixedKeys - Column keys that cannot be hidden or reordered (e.g., ['id', 'action'])
 * @param {Object} [partnerDefaults] - Optional partner-level default layout
 *   ({ hidden: string[], order: string[], pageSize: number }). When set, used
 *   as the initial state when localStorage is empty AND on "Reset to defaults".
 *   Drives OVO-110 (admin pre-configures the layout per partner so partner
 *   viewers land on a sensible default without touching the gear icon).
 * @returns {{ visibleColumns: Array, drawerOpen: boolean, openDrawer: Function, closeDrawer: Function, hidden: Set, order: Array, toggleColumn: Function, moveColumn: Function, resetConfig: Function }}
 */
const DEFAULT_PAGE_SIZE = 15;

/**
 * Read the persisted page size for a table without mounting the hook.
 * Server-paginated pages need it BEFORE their first fetch, otherwise the table
 * paginates at the stored size while the request asked for a different one.
 */
export function getStoredPageSize(tableKey, fallback) {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_PREFIX + tableKey) || 'null');
    return saved?.pageSize || fallback;
  } catch {
    return fallback;
  }
}

export default function useColumnConfig(tableKey, allColumns, fixedKeys = [], partnerDefaults = null) {
  const defaultOrder = allColumns.map((col) => col.key);

  // Two flags shape behaviour:
  //   - `isImpersonating`: admin acting as a partner viewer. We bypass
  //     localStorage entirely so the admin sees exactly what a fresh partner
  //     viewer would see (and any tweaks via the gear icon don't pollute the
  //     admin's own saved preferences). Captured once at mount — it doesn't
  //     change during a session.
  //   - `partnerDefaults`: arrives via prop AFTER the partner record is
  //     fetched (one render-cycle behind initial mount), so we can't just
  //     read it inside useState's initialiser. The useEffect below handles
  //     the "defaults arrived later" case.
  const isImpersonating = useMemo(() => !!getCurrentImpersonation(), []);

  // What "no per-user config" means: prefer partner defaults if provided,
  // otherwise the column array's natural order with everything visible.
  const fallbackHidden = partnerDefaults?.hidden || [];
  const fallbackOrder = partnerDefaults?.order || defaultOrder;
  const fallbackPageSize = partnerDefaults?.pageSize || DEFAULT_PAGE_SIZE;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [config, setConfig] = useState(() => {
    // During impersonation: ignore localStorage. Admin's own preferences for
    // /admin/orders shouldn't bleed into the partner-viewer preview.
    const saved = isImpersonating ? null : loadConfig(tableKey);
    return {
      hidden: new Set(saved?.hidden || fallbackHidden),
      order: saved?.order || fallbackOrder,
      pageSize: saved?.pageSize || fallbackPageSize,
    };
  });

  // When partnerDefaults loads (or changes), re-seed config IF:
  //   - we're impersonating (always honor the partner's defaults during
  //     impersonation — the whole point of the feature), OR
  //   - we're a real partner viewer with no localStorage yet (the OVO-110
  //     "first visit" case).
  // We track which defaults we've already applied to avoid clobbering the
  // user's in-session tweaks if partnerDefaults reference changes during
  // re-renders.
  const appliedDefaultsRef = useRef(null);
  const partnerDefaultsKey = partnerDefaults ? JSON.stringify(partnerDefaults) : null;
  useEffect(() => {
    if (!partnerDefaults) return;
    if (appliedDefaultsRef.current === partnerDefaultsKey) return;
    if (!isImpersonating && loadConfig(tableKey)) return; // user has their own saved config
    appliedDefaultsRef.current = partnerDefaultsKey;
    setConfig({
      hidden: new Set(partnerDefaults.hidden || []),
      order: partnerDefaults.order || allColumns.map((c) => c.key),
      pageSize: partnerDefaults.pageSize || DEFAULT_PAGE_SIZE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerDefaultsKey, isImpersonating, tableKey]);

  const persist = useCallback(
    (newConfig) => {
      // Don't write to the admin's localStorage while they're viewing as a
      // partner — gear-icon tweaks during impersonation are session-scoped.
      if (isImpersonating) return;
      saveConfig(tableKey, {
        hidden: Array.from(newConfig.hidden),
        order: newConfig.order,
        pageSize: newConfig.pageSize,
      });
    },
    [tableKey, isImpersonating],
  );

  const toggleColumn = useCallback(
    (key) => {
      setConfig((prev) => {
        const hidden = new Set(prev.hidden);
        if (hidden.has(key)) {
          hidden.delete(key);
        } else {
          hidden.add(key);
        }
        const next = { ...prev, hidden };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const moveColumn = useCallback(
    (fromIndex, toIndex) => {
      setConfig((prev) => {
        // Build clean order matching what the drawer actually displays
        // (orderedAllColumns). CRITICAL: orderedAllColumns applies
        // pinActionToEnd, so cleanOrder MUST too — otherwise fromIndex/toIndex
        // (from the visual drawer) point to different keys than they do in
        // cleanOrder, and the wrong column gets moved. This used to break
        // when a brand-new column (e.g. pickupName added later) got appended
        // after the saved order's `action`, leaving cleanOrder as
        // [..., action, pickupName] while the drawer rendered
        // [..., pickupName, action].
        const colMap = new Map(allColumns.map((col) => [col.key, col]));
        const cleanOrder = [];
        for (const key of prev.order) {
          if (colMap.has(key)) {
            cleanOrder.push(key);
            colMap.delete(key);
          }
        }
        for (const [key] of colMap) {
          cleanOrder.push(key);
        }
        const aligned = cleanOrder.filter((k) => k !== 'action');
        if (cleanOrder.includes('action')) aligned.push('action');
        // Now splice on the aligned order so indices match the visual list
        const [moved] = aligned.splice(fromIndex, 1);
        aligned.splice(toIndex, 0, moved);
        const next = { ...prev, order: aligned };
        persist(next);
        return next;
      });
    },
    [persist, allColumns],
  );

  const setPageSize = useCallback(
    (size) => {
      setConfig((prev) => {
        const next = { ...prev, pageSize: size };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const resetConfig = useCallback(() => {
    // "Reset to defaults" falls back to the partner default if one is
    // configured for this table, otherwise the natural order. Matches the
    // OVO-110 acceptance criteria — the partner-admin pre-config becomes the
    // floor that viewers reset to.
    const next = {
      hidden: new Set(fallbackHidden),
      order: fallbackOrder,
      pageSize: fallbackPageSize,
    };
    setConfig(next);
    persist(next);
  }, [fallbackHidden, fallbackOrder, fallbackPageSize, persist]);

  // Action column always goes last, regardless of saved order or other right-fixed columns.
  const pinActionToEnd = (cols) => {
    const main = [];
    const action = [];
    for (const c of cols) {
      if (c.key === 'action') action.push(c);
      else main.push(c);
    }
    return [...main, ...action];
  };

  const visibleColumns = useMemo(() => {
    const colMap = new Map(allColumns.map((col) => [col.key, col]));
    const ordered = [];

    // Add columns in saved order
    for (const key of config.order) {
      if (colMap.has(key) && !config.hidden.has(key)) {
        ordered.push(colMap.get(key));
        colMap.delete(key);
      } else {
        colMap.delete(key);
      }
    }

    // Add any new columns not in saved order (appended at end, visible by default)
    for (const [key, col] of colMap) {
      if (!config.hidden.has(key)) {
        ordered.push(col);
      }
    }

    return pinActionToEnd(ordered);
  }, [allColumns, config]);

  // Ordered list of all column keys (for the drawer) — includes hidden ones
  const orderedAllColumns = useMemo(() => {
    const colMap = new Map(allColumns.map((col) => [col.key, col]));
    const result = [];

    for (const key of config.order) {
      if (colMap.has(key)) {
        result.push(colMap.get(key));
        colMap.delete(key);
      }
    }
    for (const [, col] of colMap) {
      result.push(col);
    }

    return pinActionToEnd(result);
  }, [allColumns, config.order]);

  return {
    visibleColumns,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    hidden: config.hidden,
    order: config.order,
    pageSize: config.pageSize,
    orderedAllColumns,
    fixedKeys,
    toggleColumn,
    moveColumn,
    setPageSize,
    resetConfig,
  };
}
